import type { CampaignSpec } from '@vagusx/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCampaignVersions } from '@/hooks/use-outreach';
import { summariseSequence } from './spec';

/**
 * Version history, and the way back to a good one.
 *
 * "Restore" loads the version into the editor rather than activating it —
 * publishing it creates a NEW version on top. Versions are immutable and runs
 * pin the one they started on, so silently re-pointing at an old version would
 * change what a patient mid-sequence receives.
 */
export function VersionsDialog({
  campaignId,
  open,
  onOpenChange,
  onRestore,
}: {
  campaignId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore: (spec: CampaignSpec) => void;
}): React.ReactElement {
  const { data: versions, isLoading } = useCampaignVersions(open ? campaignId : undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Versions</DialogTitle>
          <DialogDescription>
            Every publish creates one. A patient already mid-sequence stays on the version their run
            started with.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-96 space-y-2 overflow-y-auto">
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {versions?.length === 0 && (
            <p className="text-sm text-muted-foreground">Nothing published yet.</p>
          )}

          {versions?.map((version) => (
            <div
              key={version.id}
              className="flex items-start justify-between gap-4 rounded-md border p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">v{version.version}</span>
                  {version.label && <span className="text-sm">{version.label}</span>}
                  {version.liveRuns > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {version.liveRuns} {version.liveRuns === 1 ? 'run' : 'runs'} still on it
                    </Badge>
                  )}
                  {version.editedAfterGeneration && (
                    <Badge variant="outline" className="text-xs">
                      hand-edited
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {new Date(version.createdAt).toLocaleString()} · {summariseSequence(version.spec)}
                </div>
                {/*
                  A version published under an older shape no longer parses. It
                  is shown as unreadable rather than taking the whole history
                  down — this is exactly the page someone needs in order to roll
                  back to a good one.
                */}
                {!version.spec && (
                  <div className="mt-1 text-xs text-destructive">
                    Saved in a format this version of the app cannot read.
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                size="sm"
                disabled={!version.spec}
                onClick={() => version.spec && onRestore(version.spec)}
              >
                Load into editor
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
